import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polygon, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

const DrawingHandler = ({ isDrawing, onPointAdded, drawingPoints, onFinishDrawing }) => {
  useMapEvents({
    click: (event) => {
      if (isDrawing) {
        const { lat, lng } = event.latlng;
        onPointAdded([lat, lng]);
      }
    },
    dblclick: (event) => {
      if (isDrawing && drawingPoints.length >= 3) {
        event.originalEvent.preventDefault();
        onFinishDrawing();
      }
    }
  });
  return null;
};

const AdminZoneDrawer = ({ 
  existingZones = [], 
  onZonesSaved,
  center = [20, 0],
  zoom = 2
}) => {
  const [zones, setZones] = useState(existingZones);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingPoints, setDrawingPoints] = useState([]);
  const [editingZone, setEditingZone] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  const [zoneForm, setZoneForm] = useState({
    name: '',
    level: 'safe',
    description: '',
    crimeRate: '',
    policePresence: '',
    touristSafety: '',
    recommendations: ['']
  });

  const mapRef = useRef(null);

  const safetyLevels = {
    safe: { color: '#4CAF50', label: 'Safe' },
    caution: { color: '#FFC107', label: 'Caution' },
    unsafe: { color: '#F44336', label: 'Unsafe' }
  };

  const resetForm = () => {
    setZoneForm({
      name: '',
      level: 'safe',
      description: '',
      crimeRate: '',
      policePresence: '',
      touristSafety: '',
      recommendations: ['']
    });
  };

  const handleStartDrawing = () => {
    setIsDrawing(true);
    setDrawingPoints([]);
    setSelectedZone(null);
    setEditingZone(null);
    resetForm();
  };

  const handlePointAdded = (point) => {
    setDrawingPoints([...drawingPoints, point]);
  };

  const handleFinishDrawing = () => {
    if (drawingPoints.length >= 3) {
      setIsDrawing(false);
      setShowForm(true);
    }
  };

  const handleCancelDrawing = () => {
    setIsDrawing(false);
    setDrawingPoints([]);
    setShowForm(false);
    resetForm();
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    const newZone = {
      id: editingZone ? editingZone.id : `zone-${Date.now()}`,
      name: zoneForm.name,
      positions: editingZone ? editingZone.positions : [...drawingPoints, drawingPoints[0]],
      color: safetyLevels[zoneForm.level].color,
      fillColor: safetyLevels[zoneForm.level].color,
      fillOpacity: 0.3,
      level: zoneForm.level,
      safetyInfo: {
        crimeRate: zoneForm.crimeRate,
        policePresence: zoneForm.policePresence,
        touristSafety: zoneForm.touristSafety,
        description: zoneForm.description,
        recommendations: zoneForm.recommendations.filter(rec => rec.trim() !== ''),
        lastUpdated: new Date().toISOString().split('T')[0]
      }
    };

    let updatedZones;
    if (editingZone) {
      updatedZones = zones.map(zone => zone.id === editingZone.id ? newZone : zone);
    } else {
      updatedZones = [...zones, newZone];
    }

    setZones(updatedZones);
    if (onZonesSaved) {
      onZonesSaved(updatedZones);
    }

    handleCancelDrawing();
    setEditingZone(null);
  };

  const handleEditZone = (zone) => {
    setEditingZone(zone);
    setZoneForm({
      name: zone.name,
      level: zone.level,
      description: zone.safetyInfo.description,
      crimeRate: zone.safetyInfo.crimeRate,
      policePresence: zone.safetyInfo.policePresence,
      touristSafety: zone.safetyInfo.touristSafety,
      recommendations: zone.safetyInfo.recommendations.length > 0 ? zone.safetyInfo.recommendations : ['']
    });
    setShowForm(true);
  };

  const handleDeleteZone = (zoneId) => {
    if (window.confirm('Are you sure you want to delete this zone?')) {
      const updatedZones = zones.filter(zone => zone.id !== zoneId);
      setZones(updatedZones);
      if (onZonesSaved) {
        onZonesSaved(updatedZones);
      }
      setSelectedZone(null);
    }
  };

  const handleZoneClick = (zone) => {
    setSelectedZone(zone);
  };

  const handleRecommendationChange = (index, value) => {
    const newRecommendations = [...zoneForm.recommendations];
    newRecommendations[index] = value;
    setZoneForm({ ...zoneForm, recommendations: newRecommendations });
  };

  const addRecommendation = () => {
    setZoneForm({ 
      ...zoneForm, 
      recommendations: [...zoneForm.recommendations, ''] 
    });
  };

  const removeRecommendation = (index) => {
    const newRecommendations = zoneForm.recommendations.filter((_, i) => i !== index);
    setZoneForm({ ...zoneForm, recommendations: newRecommendations });
  };

  useEffect(() => {
    setZones(existingZones);
  }, [existingZones]);

  return (
    <div style={{ width: '100%', height: '100vh', display: 'flex' }}>
      <div style={{ 
        width: '300px', 
        backgroundColor: '#f5f5f5', 
        padding: '20px',
        overflowY: 'auto',
        borderRight: '1px solid #ddd'
      }}>
        <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#333' }}>
          Zone Admin Panel
        </h2>

        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={handleStartDrawing}
            disabled={isDrawing}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: isDrawing ? '#ccc' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isDrawing ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {isDrawing ? 'Drawing Mode Active' : 'Start Drawing Zone'}
          </button>
          
          {isDrawing && (
            <div style={{ marginTop: '10px' }}>
              <p style={{ fontSize: '12px', color: '#666', margin: '0 0 8px 0' }}>
                Click on map to add points. Double-click to finish.
              </p>
              <button
                onClick={handleCancelDrawing}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Cancel Drawing
              </button>
            </div>
          )}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', margin: '0 0 10px 0', color: '#333' }}>
            Existing Zones ({zones.length})
          </h3>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {zones.map((zone) => (
              <div
                key={zone.id}
                style={{
                  padding: '8px',
                  marginBottom: '8px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  border: selectedZone?.id === zone.id ? '2px solid #2196F3' : '1px solid #ddd',
                  cursor: 'pointer'
                }}
                onClick={() => handleZoneClick(zone)}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                  <span
                    style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: zone.color,
                      borderRadius: '2px',
                      marginRight: '8px'
                    }}
                  ></span>
                  <strong style={{ fontSize: '12px' }}>{zone.name}</strong>
                </div>
                <div style={{ fontSize: '10px', color: '#666' }}>
                  Level: {safetyLevels[zone.level].label}
                </div>
                <div style={{ marginTop: '4px' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditZone(zone);
                    }}
                    style={{
                      fontSize: '10px',
                      padding: '4px 8px',
                      marginRight: '4px',
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '2px',
                      cursor: 'pointer'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteZone(zone.id);
                    }}
                    style={{
                      fontSize: '10px',
                      padding: '4px 8px',
                      backgroundColor: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '2px',
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {showForm && (
          <div style={{
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              width: '400px',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}>
              <h3 style={{ margin: '0 0 15px 0' }}>
                {editingZone ? 'Edit Zone' : 'Add Zone Details'}
              </h3>
              
              <form onSubmit={handleFormSubmit}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
                    Zone Name *
                  </label>
                  <input
                    type="text"
                    value={zoneForm.name}
                    onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
                    Safety Level *
                  </label>
                  <select
                    value={zoneForm.level}
                    onChange={(e) => setZoneForm({ ...zoneForm, level: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    {Object.entries(safetyLevels).map(([key, value]) => (
                      <option key={key} value={key}>{value.label}</option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
                    Description
                  </label>
                  <textarea
                    value={zoneForm.description}
                    onChange={(e) => setZoneForm({ ...zoneForm, description: e.target.value })}
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
                    Crime Rate
                  </label>
                  <select
                    value={zoneForm.crimeRate}
                    onChange={(e) => setZoneForm({ ...zoneForm, crimeRate: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select...</option>
                    <option value="Very Low">Very Low</option>
                    <option value="Low">Low</option>
                    <option value="Moderate">Moderate</option>
                    <option value="High">High</option>
                    <option value="Very High">Very High</option>
                  </select>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
                    Police Presence
                  </label>
                  <select
                    value={zoneForm.policePresence}
                    onChange={(e) => setZoneForm({ ...zoneForm, policePresence: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select...</option>
                    <option value="Very Low">Very Low</option>
                    <option value="Low">Low</option>
                    <option value="Moderate">Moderate</option>
                    <option value="High">High</option>
                    <option value="Very High">Very High</option>
                  </select>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
                    Tourist Safety
                  </label>
                  <select
                    value={zoneForm.touristSafety}
                    onChange={(e) => setZoneForm({ ...zoneForm, touristSafety: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select...</option>
                    <option value="Very Safe">Very Safe</option>
                    <option value="Generally Safe">Generally Safe</option>
                    <option value="Exercise Caution">Exercise Caution</option>
                    <option value="Not Recommended">Not Recommended</option>
                    <option value="Avoid">Avoid</option>
                  </select>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
                    Safety Recommendations
                  </label>
                  {zoneForm.recommendations.map((rec, index) => (
                    <div key={index} style={{ display: 'flex', marginBottom: '8px' }}>
                      <input
                        type="text"
                        value={rec}
                        onChange={(e) => handleRecommendationChange(index, e.target.value)}
                        placeholder="Enter recommendation"
                        style={{
                          flex: 1,
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          marginRight: '8px'
                        }}
                      />
                      {zoneForm.recommendations.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRecommendation(index)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addRecommendation}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    + Add Recommendation
                  </button>
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      padding: '10px',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    {editingZone ? 'Update Zone' : 'Save Zone'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelDrawing}
                    style={{
                      flex: 1,
                      padding: '10px',
                      backgroundColor: '#757575',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      <div style={{ flex: 1, position: 'relative' }}>
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ width: '100%', height: '100%' }}
          zoomControl={true}
          scrollWheelZoom={true}
          doubleClickZoom={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <DrawingHandler
            isDrawing={isDrawing}
            onPointAdded={handlePointAdded}
            drawingPoints={drawingPoints}
            onFinishDrawing={handleFinishDrawing}
          />

          {zones.map((zone) => (
            <Polygon
              key={zone.id}
              positions={zone.positions}
              pathOptions={{
                color: zone.color,
                fillColor: zone.fillColor,
                fillOpacity: zone.fillOpacity,
                weight: selectedZone?.id === zone.id ? 3 : 2,
                opacity: 0.8
              }}
              eventHandlers={{
                click: () => handleZoneClick(zone)
              }}
            />
          ))}

          {isDrawing && drawingPoints.length > 0 && (
            <Polygon
              positions={drawingPoints}
              pathOptions={{
                color: safetyLevels[zoneForm.level].color,
                fillColor: safetyLevels[zoneForm.level].color,
                fillOpacity: 0.2,
                weight: 2,
                opacity: 0.8,
                dashArray: '5, 5'
              }}
            />
          )}
        </MapContainer>

        {isDrawing && (
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '10px',
            borderRadius: '4px',
            fontSize: '12px',
            zIndex: 1000
          }}>
            Drawing Mode: Click to add points
            <br />
            Points: {drawingPoints.length}
            <br />
            Double-click to finish
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminZoneDrawer;